/**
 * AuthEngine.jsx — Iter-302 IMMUTABLE
 *
 * Cocoon Nest — Security & Role single source of truth. Every UI surface
 * that asks a security question — "is this user the Owner?", "can this
 * role see the Automated Notifications menu?", "can this actor assign
 * this role to another staff member?", "has the Manager PIN been
 * re-verified in the last 5 minutes?" — MUST route through the helpers
 * exported here. No inline duplication of the checks anywhere else in
 * the codebase (see SYSTEM_RULES.md → "Security Logic lives strictly in
 * AuthEngine.jsx. NO elevation of privileges or menu-unhiding allowed
 * during UI tasks.")
 *
 * Scope of ownership (all consumers pass this through the engine):
 *   1. Permission Matrix — <item>.<action> keys with the legacy-alias
 *      compatibility table. `hasPermission`, `isOwner`, `isOwnerOrAdmin`.
 *   2. Role Hierarchy — Owner > Admin > Manager > Supervisor > Staff.
 *      `getHierarchyLevel`, `outranks`, `canAssignRole`,
 *      `rolesAssignableBy`, `MANAGEMENT_ROLE_IDS`, `CARE_TEAM_ROLE_ID`.
 *   3. Owner-Only Gates — Launch Reset, Brand · Branches, Automated
 *      Notifications. `filterOwnerOnlyItems`, `canAccessLaunchReset`,
 *      `canAccessBrandBranches`, `canAccessAutomatedNotifications`.
 *   4. Menu Visibility — the Absolute Hiding rule (if a role does NOT
 *      hold the view action the entry is REMOVED from the DOM, not
 *      greyed out). `filterMenuByPermissions`.
 *   5. Manager PIN Gate — Report Export re-auth. `PIN_TTL_MS`,
 *      `isExportPinFresh`, `stampExportPin`, `clearExportPin`.
 *   6. Personal-View-Only Landing — Care Team practitioners land on the
 *      mobile roster instead of the branch picker. `isPersonalViewOnly`,
 *      `calendarViewScope`.
 *   7. Brand Workspace Gate — `BRAND_PERMISSIONS`, `hasAnyBrandPermission`.
 *
 * Purity contract: no React state, no I/O, no DOM. Callers pass in the
 * `user` / `role` objects fetched from `/auth/me` and receive booleans
 * or filtered arrays back. Live state refresh is the caller's job (via
 * `useAuth().refreshStaff()` — the AuthEngine reads the current user
 * object supplied on every call and never caches).
 *
 * Global scope: applies to all 8 branches (Ubud, Canggu, Seminyak, and
 * any future openings). One engine, one behaviour, forever.
 */

// ─────────────────────────────────────────────────────────────────────
// Role Hierarchy — Owner > Admin > Manager > Supervisor > Staff
// ─────────────────────────────────────────────────────────────────────

export const OWNER_ROLE_ID = "role_owner";
export const ADMIN_ROLE_ID = "role_admin";
export const MANAGER_ROLE_ID = "role_preset_manager";
export const SUPERVISOR_ROLE_ID = "role_preset_supervisor";
export const RESERVATION_ROLE_ID = "role_preset_reservation";
export const STORE_ROLE_ID = "role_preset_store";
export const CARE_TEAM_ROLE_ID = "role_preset_care_team";

/**
 * Administrative preset roles that always list under "Active Management
 * Team" (never appear on the booking calendar as a bookable specialist).
 */
export const MANAGEMENT_ROLE_IDS = new Set([
  OWNER_ROLE_ID,
  ADMIN_ROLE_ID,
  MANAGER_ROLE_ID,
  SUPERVISOR_ROLE_ID,
  RESERVATION_ROLE_ID,
  STORE_ROLE_ID,
]);

/**
 * Canonical hierarchy levels used when a role document does not carry
 * `hierarchy_level` (defensive fallback). Higher number = more power.
 * Owner sits at 100; Admin at 90; Manager 70; Supervisor 50; everyone
 * else 10.
 */
const PRESET_LEVELS = {
  [OWNER_ROLE_ID]: 100,
  [ADMIN_ROLE_ID]: 90,
  [MANAGER_ROLE_ID]: 70,
  [SUPERVISOR_ROLE_ID]: 50,
  [RESERVATION_ROLE_ID]: 30,
  [STORE_ROLE_ID]: 30,
  [CARE_TEAM_ROLE_ID]: 10,
};

// ─────────────────────────────────────────────────────────────────────
// Legacy permission-key aliases
// The backend catalog was flattened to <item>.<action>. Callers still
// asking for the old strings get routed through this table so ANY of
// the mapped new keys unlocks them.
// ─────────────────────────────────────────────────────────────────────

const LEGACY_ALIAS = {
  "roles.manage_roles":                ["brand.staff_logins.edit", "brand.staff_logins.create"],
  "employees.create_staff":            ["brand.staff_logins.create"],
  "employees.edit_staff":              ["brand.staff_logins.edit"],
  "employees.delete_staff":            ["brand.staff_logins.delete"],
  "operations.manage_services":        ["brand.services.create", "brand.services.edit", "brand.services.delete"],
  "operations.manage_rooms":           ["branch_settings.rooms.create", "branch_settings.rooms.edit", "branch_settings.rooms.delete"],
  "operations.manage_schedules":       ["location.schedule.create", "location.schedule.edit", "location.schedule.delete"],
  // Removed 2026-08 — the generic "Calendar" row was a confusing
  // duplicate of "Booking Calendar". Redirect stored/legacy keys.
  "location.calendar.create":          ["location.booking_calendar.create"],
  "location.calendar.view":            ["location.booking_calendar.view"],
  "location.calendar.edit":            ["location.booking_calendar.edit"],
  "location.calendar.delete":          ["location.booking_calendar.delete"],
  "financials.view_reports":           ["brand.overview.view"],
  "financials.view_orders":            ["location.orders_pos.view"],
  "financials.process_refunds_voids":  ["location.orders_pos.delete"],
  "financials.manage_commissions":     ["brand.overview.edit"],
  "customers.view_edit":               ["brand.customers.view", "brand.customers.edit"],
  "customers.merge_preview":           ["brand.customers.delete"],
  "customers.delete_download":         ["brand.customers.delete"],
};

// ─────────────────────────────────────────────────────────────────────
// Permission Matrix — Create / View / Edit / Delete
// ─────────────────────────────────────────────────────────────────────

/** Owner short-circuit. Honours `user.is_owner`. */
export function isOwner(user) {
  return Boolean(user && user.is_owner);
}

/**
 * Owner OR Admin (`role_id === role_admin`). Used by high-privilege
 * surfaces (Report Permissions column, purge tools, hierarchy overrides)
 * that regular managers must not see even when they hold specific
 * granular perms.
 */
export function isOwnerOrAdmin(user) {
  if (!user) return false;
  if (user.is_owner) return true;
  return (user.role_id || user.role) === ADMIN_ROLE_ID;
}

/**
 * Canonical permission check. Owner always wins (full override). Missing
 * perms fall back to `false`. Legacy keys route through LEGACY_ALIAS —
 * ANY mapped new key unlocks the caller.
 */
export function hasPermission(user, key) {
  if (!user) return false;
  if (user.is_owner) return true;
  const perms = user.permissions || {};
  if (perms[key]) return true;
  const aliases = LEGACY_ALIAS[key] || [];
  return aliases.some((k) => Boolean(perms[k]));
}

export function assignedBranchIds(user) {
  return (user && user.branch_ids) || [];
}

// ─────────────────────────────────────────────────────────────────────
// Calendar Scope — Personal vs Global
// ─────────────────────────────────────────────────────────────────────

/**
 * Resolve which calendar view scopes a staff member can use. Owner
 * always gets both. When only one is granted, the calendar is forced
 * into that mode. When both are on, the calendar shows a toggle so the
 * user can switch between "My schedule" and "Branch view".
 */
export function calendarViewScope(user) {
  const personal = hasPermission(user, "location.booking_calendar.personal_view");
  const global = hasPermission(user, "location.booking_calendar.global_view");
  return {
    personal,
    global,
    canToggle: personal && global,
    // Default mode when both are false: no calendar access at all.
    // When only personal is true → personal. Otherwise → global.
    defaultMode: global ? "global" : (personal ? "personal" : null),
  };
}

/**
 * Personal-view-only users (Care Team practitioners) land on the mobile
 * 10-day roster instead of the branch picker. Owners and global-view
 * users always see the standard landing.
 */
export function isPersonalViewOnly(user) {
  if (!user) return false;
  if (isOwner(user)) return false;
  const scope = calendarViewScope(user);
  return scope.personal && !scope.global;
}

// ─────────────────────────────────────────────────────────────────────
// Brand Workspace Gate
// Any one of the BRAND_PERMISSIONS unlocks the Brand shell. Owner always
// wins via the is_owner short-circuit in hasPermission.
// ─────────────────────────────────────────────────────────────────────

export const BRAND_PERMISSIONS = [
  "brand.overview.view",
  "brand.staff_logins.view",
  "brand.staff_logins.edit",
  "brand.staff_logins.create",
  "brand.services.view",
  "brand.packages.view",
  "brand.products.view",
  "brand.series.view",
  "brand.customers.view",
  "brand.gift_card.view",
  "brand.loyalty_rewards.view",
  "brand.general.view",
];

export function hasAnyBrandPermission(user) {
  if (isOwner(user)) return true;
  return BRAND_PERMISSIONS.some((k) => hasPermission(user, k));
}

// ─────────────────────────────────────────────────────────────────────
// Owner-Only Gates
// The hardest gates on the app. Not even a Brand Admin may reach these
// surfaces by typing the URL — StaffRoute + this engine enforce it in
// two layers.
// ─────────────────────────────────────────────────────────────────────

/** Launch Reset — brand/branch data-wipe demolition tool. Owner-only. */
export function canAccessLaunchReset(user) {
  return isOwner(user);
}

/** Brand · Branches management page. Owner-only. */
export function canAccessBrandBranches(user) {
  return isOwner(user);
}

/** Automated Notifications Command Center. Owner-only. */
export function canAccessAutomatedNotifications(user) {
  return isOwner(user);
}

/**
 * Menu-visibility filter — the Absolute Hiding rule.
 *
 * Input: a list of menu entries where each entry may carry ONE of:
 *   - `ownerOnly: true`           → visible only to the Owner
 *   - `gate: <boolean>`           → pre-computed external gate
 *   - `perm: "<item>.<action>"`   → gated by hasPermission(user, key)
 *   - no gate                     → always visible
 *
 * Output: filtered array. Removed entries are STRIPPED from the DOM,
 * never greyed out (per SYSTEM_RULES → Permissions → Global Hiding).
 */
export function filterMenuByPermissions(items, user) {
  return (items || []).filter((l) => {
    if (l.ownerOnly) return isOwner(user);
    if ("gate" in l) return !!l.gate;
    return !l.perm || hasPermission(user, l.perm);
  });
}

/** Alias — kept for symmetry with the CheckIn / Package / Billing engines. */
export const filterOwnerOnlyItems = filterMenuByPermissions;

// ─────────────────────────────────────────────────────────────────────
// Role Hierarchy Helpers
// ─────────────────────────────────────────────────────────────────────

/**
 * Resolve a role's hierarchy_level. Prefers the value persisted on the
 * role document; falls back to PRESET_LEVELS when missing (defensive —
 * a fresh install may not have the level baked into the preset). The
 * Owner role always resolves to Infinity so the comparison predicates
 * never let a peer outrank the Owner.
 */
export function getHierarchyLevel(role) {
  if (!role) return 0;
  if (role.is_owner) return Number.POSITIVE_INFINITY;
  if (typeof role.hierarchy_level === "number") return role.hierarchy_level;
  return PRESET_LEVELS[role.id] || 0;
}

/** Does the actor outrank the target role? Owner always outranks. */
export function outranks(actor, targetRole) {
  if (isOwner(actor)) return true;
  const actorLevel = typeof actor?.hierarchy_level === "number"
    ? actor.hierarchy_level
    : (PRESET_LEVELS[actor?.role_id] || 0);
  const targetLevel = getHierarchyLevel(targetRole);
  return actorLevel > targetLevel;
}

/**
 * Can the actor assign this role to a staff member?
 *
 * Contract (mirrors the Brand · Staff / Roles rule): Owner can assign
 * any role. Non-owners can only assign roles at or below their own
 * hierarchy level AND may never assign the Owner role.
 */
export function canAssignRole(actor, targetRole) {
  if (!targetRole) return false;
  if (isOwner(actor)) return true;
  if (targetRole.is_owner) return false;
  const actorLevel = typeof actor?.hierarchy_level === "number" ? actor.hierarchy_level : 0;
  const targetLevel = getHierarchyLevel(targetRole);
  return targetLevel <= actorLevel;
}

/** Filter a list of role documents to those the actor may assign. */
export function rolesAssignableBy(actor, roles) {
  return (roles || []).filter((r) => canAssignRole(actor, r));
}

/** Is this role the immutable Owner? Belt-and-braces (id + flag). */
export function isRoleOwner(role) {
  if (!role) return false;
  return Boolean(role.is_owner) || role.id === OWNER_ROLE_ID;
}

// ─────────────────────────────────────────────────────────────────────
// Manager PIN Gate — Report Export re-authentication
// Every Download PDF / Download Excel click routes through this gate.
// The operator must re-enter their username + password to confirm they
// intend to remove PII from the browser. Once verified, the gate
// remembers the authorisation for PIN_TTL_MS so back-to-back exports
// aren't interrupted. The backend still records every attempt.
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// Iter-358 · Guest Portal Passwordless OTP — pure helpers
//
// The Guest Portal login flow was changed from `identifier + password`
// to `email → OTP`. AuthEngine owns the pure classification helpers so
// the surface (`pages/portal/CustomerLogin.jsx`) has zero inline auth
// rules. Staff login is UNCHANGED (username + password) and is not
// covered by these helpers.
//
// Steps encoded here:
//   PORTAL_LOGIN_STEP_EMAIL       — email input screen
//   PORTAL_LOGIN_STEP_CONFIRM_REG — "Would you like to register?" prompt
//                                    shown when the email is unknown
//   PORTAL_LOGIN_STEP_OTP         — 6-digit code input after OTP sent
// ─────────────────────────────────────────────────────────────────────

export const PORTAL_LOGIN_STEP_EMAIL = "email";
export const PORTAL_LOGIN_STEP_CONFIRM_REG = "confirm_register";
export const PORTAL_LOGIN_STEP_OTP = "otp";

/** Case-insensitive, whitespace-safe email shape check for the Guest
 *  Portal login form. NOT an authoritative validation — the backend
 *  runs pydantic's `EmailStr` — but tight enough that a typo like
 *  "you@" or "@example.com" is blocked before we round-trip. */
export function isValidPortalEmail(input) {
  if (typeof input !== "string") return false;
  const email = input.trim().toLowerCase();
  if (email.length < 5 || email.length > 254) return false;
  const at = email.indexOf("@");
  const dot = email.lastIndexOf(".");
  if (at <= 0 || dot <= at + 1 || dot >= email.length - 1) return false;
  if (/\s/.test(email)) return false;
  return true;
}

/** 6-digit numeric OTP shape guard. Enforced client-side so the user
 *  sees an inline error before we make an HTTP round-trip; the backend
 *  runs the authoritative hash comparison. */
export function isValidPortalOtp(code) {
  if (typeof code !== "string") return false;
  return /^\d{6}$/.test(code.trim());
}

/** Decide the next portal-login step given the current step + the
 *  backend's check-email result. Pure function — no I/O, no side
 *  effects. Returns one of the `PORTAL_LOGIN_STEP_*` constants. */
export function decidePortalLoginStep({ currentStep, checkEmailResult }) {
  if (!checkEmailResult) return currentStep || PORTAL_LOGIN_STEP_EMAIL;
  return checkEmailResult.exists === true
    ? PORTAL_LOGIN_STEP_OTP
    : PORTAL_LOGIN_STEP_CONFIRM_REG;
}

/**
 * Iter-359 · Real-Time Identity Sync — pure invariant helper.
 *
 * The `customers` collection IS the authentication database — the OTP
 * login endpoints (`login-check-email`, `login-request-otp`,
 * `login-verify-otp`) all read from `db.customers` on every request.
 * When a staff member edits a customer's email through
 * `PUT /api/customers/{cid}` (RBAC: `brand.customers.edit`) the NEW
 * email is immediately usable for OTP login; the OLD email is completely
 * gone from the auth record. This function answers ONE question for
 * consumers of the customer-edit UI: "will this save action replace the
 * customer's authentication identity?" It is used by the Brand ·
 * Customer detail page to render a live warning banner before the edit
 * is saved.
 *
 * Rules:
 *   - Both inputs are normalised (trim + lowercase) so " User@X.com "
 *     and "user@x.com" are considered equal.
 *   - Empty / missing old email + a new email = new identity is being
 *     GRANTED (still counts as a replace).
 *   - Empty / missing new email = no replace (kept null).
 */
export function willReplaceEmailIdentity(oldEmail, newEmail) {
  const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
  const from = norm(oldEmail);
  const to = norm(newEmail);
  if (!to) return false;
  return from !== to;
}

// ─────────────────────────────────────────────────────────────────────
// Manager PIN Gate constants — restored below the Iter-358 block.
// ─────────────────────────────────────────────────────────────────────
export const PIN_TTL_MS = 5 * 60 * 1000;              // 5 minutes
export const PIN_STAMP_KEY = "cocoon.staff.export_pin_verified_at";

export function isExportPinFresh() {
  try {
    const raw = localStorage.getItem(PIN_STAMP_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10) || 0;
    return Date.now() - ts < PIN_TTL_MS;
  } catch { return false; }
}

export function stampExportPin() {
  try { localStorage.setItem(PIN_STAMP_KEY, String(Date.now())); } catch { /* ignore */ }
}

export function clearExportPin() {
  try { localStorage.removeItem(PIN_STAMP_KEY); } catch { /* ignore */ }
}
